import React, { MouseEvent as ReactMouseEvent, Fragment, useState, useCallback } from 'react'
import { Button, Header } from 'semantic-ui-react'
import styled from 'styled-components'
import { SliderOption } from 'common/RangeSlider/'
import DynamicPlanFilter from 'common/DynamicPlanFilter'
import PlanCard from 'common/ProductCard'
import { PlanTerm, Product } from 'common/Products/types'
import { orderByDataLimit } from 'common/Products'
import { PLAN_TERMS } from 'common/Utils'

// What the chosen mode (365 Split or 365 Flex) means, under the buttons
const ModeDescription = styled.p`
  margin: 0.75em 0 1.75em;
  font-size: 0.85em;
  color: rgba(0, 0, 0, 0.6);
  text-align: center;
`

interface PlanListProps {
  plans: Product[]
  onChange: (event: ReactMouseEvent<Element, MouseEvent>, unique_id: Product['unique_id']) => void
}

const PlanList = ({ plans, onChange }: PlanListProps) => {
  const [selected, setSelected] = useState()

  // The plan lengths on offer, as fixed terms in slider order; the first is the default
  const sellable = plans.filter((plan) => plan.sku && plan.term)
  const terms = PLAN_TERMS.map(({ term }) => term).filter((term) =>
    sellable.some((plan) => plan.term === term)
  )
  const [chosenTerm, chooseTerm] = useState<PlanTerm>()
  const term = chosenTerm && terms.includes(chosenTerm) ? chosenTerm : terms[0]
  const [flex, setFlex] = useState(false)
  // Switching mode hides the other mode's plans, so drop a selection among them -
  // otherwise Continue would buy a plan the customer can no longer see. Only when there
  // is one: renew closes the picker on any onChange.
  const switchMode = useCallback(
    (event: ReactMouseEvent<Element, MouseEvent>, toFlex: boolean) => {
      if (toFlex === flex) return
      setFlex(toFlex)
      if (selected !== undefined) {
        setSelected(undefined)
        onChange(event, '')
      }
    },
    [flex, selected, onChange]
  )
  const showMonthly = useCallback(
    (event: ReactMouseEvent<Element, MouseEvent>) => switchMode(event, false),
    [switchMode]
  )
  const showFlex = useCallback(
    (event: ReactMouseEvent<Element, MouseEvent>) => switchMode(event, true),
    [switchMode]
  )

  const onPlanFilterChange = useCallback((name: string, option: SliderOption) => {
    chooseTerm(option.value as PlanTerm)
  }, [])

  const onPlanClick = useCallback(
    (event: ReactMouseEvent<Element, MouseEvent> | React.KeyboardEvent, plan: Product) => {
      setSelected(plan.unique_id)
      onChange(event as ReactMouseEvent<Element, MouseEvent>, plan.unique_id)
    },
    [onChange]
  )

  const lengthPlans = sellable.sort(orderByDataLimit).filter((plan) => plan.term === term)
  // Monthly data and Flex plans can share a length (eg. 1 year), so let the customer pick
  const hasFlexChoice =
    lengthPlans.some((plan) => plan.is_flex) && lengthPlans.some((plan) => !plan.is_flex)
  const shownPlans = hasFlexChoice
    ? lengthPlans.filter((plan) => plan.is_flex === flex)
    : lengthPlans

  return (
    <Fragment>
      <Header as="h3" color="grey">
        Choose Prepay Plan Length
      </Header>
      <DynamicPlanFilter name="planFilter" onChange={onPlanFilterChange} terms={terms} />
      <Header as="h3" color="grey">
        Choose Prepay Plan
      </Header>
      {hasFlexChoice && (
        <Fragment>
          <Button.Group fluid widths={2}>
            <Button type="button" active={!flex} onClick={showMonthly}>
              365 Split
            </Button>
            <Button type="button" active={flex} onClick={showFlex}>
              365 Flex
            </Button>
          </Button.Group>
          <ModeDescription>
            {flex
              ? 'All your data to use anytime throughout the year.'
              : 'Your data renews every month.'}
          </ModeDescription>
        </Fragment>
      )}
      {shownPlans.map((plan: Product) => (
        <PlanCard
          onClick={onPlanClick}
          active={selected === plan.unique_id}
          key={plan.unique_id}
          plan={plan}
          horizontal
        />
      ))}
    </Fragment>
  )
}

export default PlanList
